
import { defineComponent, ref, reactive, toRefs, nextTick, onActivated } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useUserInfoStore } from "@/stores/UserInfoStore";
import API from "@/apis/TPSAPI";
import { APIResultCode } from "@/models/enums/APIResultCode";
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import { Search, EditPen, Delete, Calendar, Document, ZoomIn } from "@element-plus/icons-vue";
import { usePaginatorSetup } from "@/stores/PaginatorStore";
import IReqProjectSearchCriteriaDTO from "@/models/dto/IReqProjectSearchCriteriaDTO";
import IResProjectInfoDTO from "@/models/dto/IResProjectInfoDTO";
import ProjectInfo from "@/models/ProjectInfo";
import ProjectSearchCriteria from "@/models/ProjectSearchCriteria";
import ElTable from "element-plus/lib/components/table";
import IResSiteInfoDTO from "@/models/dto/IResSiteInfoDTO";
import { compare, dateFormat, delay } from "@/plugins/utility";
import { DataTableType } from "@/models/enums/DataTableType";
import numeral from "numeral";
import moment from "moment";
import IResPeriodInfoDTO from "@/models/dto/IResPeriodInfoDTO";

export default defineComponent({
  setup() {
    // #region 外部匯入 初始 預設
    const { t } = useI18n();
    const userInfoStore = useUserInfoStore();
    const router = useRouter();
    const paginatorSetup = usePaginatorSetup();
    const tableType = DataTableType.projectTable;
    // #endregion

    // #region state
    const state = reactive({
      isTableLoading: false,
      searchCriteria: new ProjectSearchCriteria(),
      filteredProjects: [] as IResProjectInfoDTO[],
      displayProjects: [] as IResProjectInfoDTO[],
      isSiteLoading: false,

      // pagination
      currentPage: 1,
      pageSize: 20,

      // 新增彈窗
      isShowAddDialog: false,
      isAddDialogLoading: false,
      formLabelWidth: "110px",
      newProjectData: new ProjectInfo(),

      // 編輯彈窗
      selectedProjectData: new ProjectInfo(),
      isShowModifyDialog: false,
      isModifyDialogLoading: false,

      // 瀏覽彈窗
      isShowViewDialog: false,

      // 廠區select
      sites: [] as IResSiteInfoDTO[],

      // 期數select
      periods: [] as IResPeriodInfoDTO[],

      colDisplayCtrl: new Array(50).fill(true) as boolean[],
    });
    // #endregion

    // #region 建立表單ref與Validator
    const workRateValidator = (rule: any, value: any, callback: any) => {
      if (!value || value <= 0) {
        return callback(new Error(t("validation_msg.work_rate_is_required")));
      }

      if(value <= 0 ) {
        return callback(new Error(t("validation_msg.invalid_work_rate")));
      }

      // if (!(0 <= value && value <= 100.0)) {
      //   return callback(new Error(t("validation_msg.invalid_work_rate")));
      // }

      callback();
    };

    const teamNumValidator = (rule: any, value: any, callback: any) => {
      if (!value) {
        return callback(new Error(t("form.validation_msg.team_name_is_required")));
      }

      const teamNumInt = parseInt(value, 10);
      if (Number.isNaN(teamNumInt)) {
        console.log("value is not a number");
        return callback(new Error(t("form.validation_msg.please_input_a_number")));
      }

      if (!(1 <= teamNumInt && teamNumInt <= 99)) {
        return callback(new Error(t("form.validation_msg.please_input_a_number_between_1_and_99")));
      }

      state.newProjectData.teamNum = teamNumInt;
      callback();
    };
    const projectFormRules = ref({
      site: [{ required: true, message: () => t("form.validation_msg.site_is_required"), trigger: "blur" }],
      factory: [{ required: true, message: () => t("form.validation_msg.factory_is_required"), trigger: "blur" }],
      teamNum: [{ required: true, validator: teamNumValidator, trigger: "blur" }],
      workRate: [{ required: true, validator: workRateValidator, trigger: "blur" }],
      period: [{ required: true, message: () => t("form.validation_msg.period_is_required"), trigger: "blur" }],
    });

    const createProjectFormRef = ref();
    const editProjectFormRef = ref();
    const projectTableRef = ref<InstanceType<typeof ElTable>>();
    // #endregion

    // #region Hook functions
    onActivated(async () => {
      try {
        state.isTableLoading = true;
        await updateUserPreference();
        await updateSiteList();

        await filterProjectListAsync();
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isTableLoading = false;
      }
    });
    // #endregion

    //#region UI Events
    /** 按下搜尋 */
    const onSearchProjectClicked = async () => {
      try {
        state.isTableLoading = true;

        //清除table排序
        projectTableRef.value?.clearSort();

        await filterProjectListAsync();
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isTableLoading = false;
      }
    };
    /** 按下並放開Enter鍵 */
    const onInputFieldEnterKeyUp = async () => {
      await onSearchProjectClicked();
    };

    /** search 清除 */
    const onClearSearchCriteriaClicked = () => {
      state.searchCriteria.clear();
    };

    /** 顯示廠區下拉選單時 */
    const onSiteListShown = async () => {
      try {
        state.isSiteLoading = true;
        await updateSiteList();
      } catch (error) {
        console.log(error);
      } finally {
        state.isSiteLoading = false;
      }
    };

    /** 下拉選單連動 */
    const onChangeSelect = (factorys: string[]) => {
      //state.factorys = factorys;
    };

    /** 按下改善事項超連結, 跳轉頁面至改善事項，同時帶入當前的傳案代碼 */
    const onKaizenItemLinkClicked = (target: IResProjectInfoDTO) => {
      //console.log("onKaizenItemLinkClicked : " + target.pjCode);
      router.push({
        name: "kaizen_list2",
        params: {
          pjcode: target.pjCode,
        },
      });
    };

    /** 新增彈窗 */
    const onCreateProjectClicked = async () => {
      await updateSiteList();
      await updatePeriodList();

      //初始化欄位內容
      state.newProjectData.creator = {
        id: userInfoStore.userId,
        username: userInfoStore.username,
        displayName: userInfoStore.displayName,
      };
      state.newProjectData.period = state.periods[0];
      state.newProjectData.site = undefined;
      state.newProjectData.factory = undefined;
      state.newProjectData.teamNum = undefined;
      state.newProjectData.workRate = undefined;
      state.newProjectData.remark = undefined;
      state.isShowAddDialog = true;

      await nextTick();
      createProjectFormRef.value?.clearValidate();
    };

    /** 新增 送出 */
    const onSubmitNewProjectClicked = async () => {
      createProjectFormRef.value?.validate(async (valid: boolean) => {
        if (!valid) return;

        try {
          await ElMessageBox.confirm(t("Create_Confirm_Msg"), t("Warning"), {
            confirmButtonText: t("Confirm"),
            cancelButtonText: t("Cancel"),
            type: "warning",
          });
        } catch (error) {
          return;
        }

        try {
          state.isAddDialogLoading = true;
          const createProjectResult = await API.createProject(state.newProjectData.ToIReqProjectInfoDTO());

          if (createProjectResult.data.result === 613) {
            //錯誤提示: 專案衝突提示
            ElMessage({
              type: "error",
              message: createProjectResult.data.msg,
            });
            throw new Error(createProjectResult.data.msg);
          }

          if (createProjectResult.data.result !== APIResultCode.success) {
            throw new Error(createProjectResult.data.msg);
          }

          ElMessage({
            showClose: true,
            message: "新增成功",
            type: "success",
          });

          // 關閉彈窗
          state.isShowAddDialog = false;

          // // 表單regex重置
          // createProjectFormRef.value.resetFields();

          // 刷新
          state.isTableLoading = true;
          await filterProjectListAsync();
        } catch (error) {
          console.log(error);
          ElMessage({
            type: "error",
            message: "處理失敗，請查看詳細訊息",
          });
        } finally {
          state.isAddDialogLoading = false;
          state.isTableLoading = false;
        }
      });
    };

    /** 編輯彈窗 */
    const onEditProjectClicked = async (target: IResProjectInfoDTO) => {
      try {
        state.isTableLoading = true;

        // await updateSiteList();
        // await updatePeriodList();
        await getSelectedProjectDetail(target.id);

        state.isShowModifyDialog = true;
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isTableLoading = false;
      }
    };

    /** 編輯 送出 */
    const onSaveModifyResultClicked = async () => {
      editProjectFormRef.value?.validate(async (valid: boolean) => {
        if (!valid) return;

        try {
          await ElMessageBox.confirm(t("update_confirm_msg"), t("Warning"), {
            confirmButtonText: t("Confirm"),
            cancelButtonText: t("Cancel"),
            type: "warning",
          });
        } catch (error) {
          return;
        }

        try {
          state.isModifyDialogLoading = true;

          const updateProjectResult = await API.updateProject(state.selectedProjectData.id, state.selectedProjectData.ToIReqProjectInfoDTO());

          if (updateProjectResult.data.result === 603) {
            //錯誤提示: 專案衝突提示
            ElMessage({
              type: "error",
              message: updateProjectResult.data.msg,
            });
          }
          if (updateProjectResult.data.result !== APIResultCode.success) {
            throw new Error(updateProjectResult.data.msg);
          }

          ElMessage({
            showClose: true,
            message: "新增成功",
            type: "success",
          });

          // 關閉彈窗
          state.isShowModifyDialog = false;

          // // 表單regex重置
          // createProjectFormRef.value.resetFields();

          // 刷新
          state.isTableLoading = true;
          await filterProjectListAsync();
        } catch (error) {
          console.log(error);
          ElMessage({
            type: "error",
            message: "處理失敗，請查看詳細訊息",
          });
        } finally {
          state.isModifyDialogLoading = false;
          state.isTableLoading = false;
        }
      });
    };

    /** 瀏覽彈窗 */
    const onViewProjectClicked = async (target: IResProjectInfoDTO) => {
      try {
        state.isTableLoading = true;

        //await updateSiteList();
        await getSelectedProjectDetail(target.id);

        state.isShowViewDialog = true;
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isTableLoading = false;
      }
    };

    /** 刪除彈窗 */
    const onDeleteProjectClicked = async (target: IResProjectInfoDTO) => {
      try {
        await ElMessageBox.confirm(t("Delete_Confirm_Msg"), t("Warning"), {
          confirmButtonText: t("Confirm"),
          cancelButtonText: t("Cancel"),
          type: "warning",
        });
      } catch (error) {
        console.log(error);
        return;
      }

      try {
        // 刪除
        state.isTableLoading = true;

        const deleteResponse = await API.deleteProject(target.id);
        if (deleteResponse.data.result == APIResultCode.item_is_being_used) {
          const kaizenItemCodeList = deleteResponse.data.content;
          ElNotification({
            title: t("error"),
            message:  t("delete_fail")+": "+ t("project_is_being_used"),
            type: "error",
          });
          return;
        }

        if (deleteResponse.data.result !== APIResultCode.success) {
          throw new Error(deleteResponse.data.msg);
        }

        ElMessage({
          showClose: true,
          message: t("delete_success"),
          type: "success",
        });

        await filterProjectListAsync();
      } catch (error) {
        console.log(error);
        ElMessage({
          type: "error",
          message: "處理失敗，請查看詳細訊息",
        });
      } finally {
        state.isTableLoading = false;
      }
    };

    /** 改變排序 */
    const onSortChanged = (sort: any) => {
      //console.log(`onSortChanged: ${sort.column}, ${sort.prop}, ${sort.order}`);
      try {
        state.isTableLoading = true;
        state.filteredProjects.sort(compare(sort.prop, sort.order));
        updateDisplayList();
      } catch (error) {
        console.log(error);
      } finally {
        state.isTableLoading = false;
      }
    };

    /** 分頁輸入 */
    const onPagesizeChanged = () => {
      updateDisplayList();
    };

    const onCurrentPageChanged = () => {
      updateDisplayList();
    };

    /** 關閉欄位顯示設定Panel時 */
    const onColDisplayCtrlPanelHidded = async () => {
      await API.patchTableDisplayPreference(tableType, { displayMap: state.colDisplayCtrl });
    };
    //#endregion

    //#region Private Functions
    /** 更新使用者設置偏好 */
    const updateUserPreference = async () => {
      const getPrefResponse = await API.getTableDisplayPreference(tableType);
      if (getPrefResponse.data.result !== 1) {
        console.log(`updateUserPreference : ${getPrefResponse.data.msg}`);
      }
      state.colDisplayCtrl = getPrefResponse.data.content;
    };

    /** 呼叫API, 更新廠區清單 */
    const updateSiteList = async () => {
      try {
        const getSiteResult = await API.getAllSites();

        if (getSiteResult.data.result !== APIResultCode.success) {
          throw new Error(getSiteResult.data.msg);
        }

        state.sites = getSiteResult.data.content;
      } catch (error) {
        console.log(error);
      }
    };

    /** 呼叫API, 更新期數清單 */
    const updatePeriodList = async () => {
      try {
        const getPeriodsResult = await API.getAvailablePeriodItems();

        if (getPeriodsResult.data.result !== APIResultCode.success) {
          throw new Error(getPeriodsResult.data.msg);
        }

        state.periods = getPeriodsResult.data.content;
      } catch (error) {
        console.log(error);
      }
    };

    /** 更新搜尋結果 */
    const filterProjectListAsync = async () => {
      if (process.env.VUE_APP_RUN_ENV === "DEV") await delay(1000);

      const searchCriteriaDTO = state.searchCriteria.ToReqDTO();
      await getFilteredProjects(searchCriteriaDTO);

      updateDisplayList();
    };

    /** cellstyle */
    const cellStyle = (column: any) => {
      if (column.label === "改善項目") {
        return {
          color: "#2F80ED",
        };
      }
    };

    /** 呼叫API, 取得搜尋結果 */
    const getFilteredProjects = async (searchCriteria: IReqProjectSearchCriteriaDTO) => {
      /* 若 開發時 等待 1 秒 */
      if (process.env.VUE_APP_RUN_ENV === "DEV") await delay(1000);

      const projectSearchResult = await API.searchProject(searchCriteria);
      if (projectSearchResult.data.result !== APIResultCode.success) {
        throw new Error(projectSearchResult.data.msg);
      }
      state.filteredProjects = projectSearchResult.data.content;
    };

    /** 呼叫API, 取得一特定傳案的細項 */
    const getSelectedProjectDetail = async (projectId: number) => {
      /* 若 開發時 等待 1 秒 */
      if (process.env.VUE_APP_RUN_ENV === "DEV") await delay(1000);

      const getProjectDetailResult = await API.getProjectById(projectId);
      if (getProjectDetailResult.data.result !== APIResultCode.success) {
        throw new Error(getProjectDetailResult.data.msg);
      }

      const pjDtl = getProjectDetailResult.data.content;
      state.selectedProjectData.id = pjDtl.id;
      state.selectedProjectData.creator = pjDtl.creator;
      state.selectedProjectData.pjcode = pjDtl.pjCode;
      //state.selectedProjectData.period = pjDtl.periodNum;
      state.selectedProjectData.period = pjDtl.period;
      state.selectedProjectData.site = pjDtl.site;
      state.selectedProjectData.factory = pjDtl.factory;
      state.selectedProjectData.teamNum = pjDtl.teamNum;
      state.selectedProjectData.workRate = pjDtl.workRate;
      state.selectedProjectData.remark = pjDtl.remark;
    };

    /** 更新顯示清單 */
    const updateDisplayList = () => {
      state.displayProjects = state.filteredProjects.slice((state.currentPage - 1) * state.pageSize, state.currentPage * state.pageSize);
    };

    //#endregion

    return {
      ...toRefs(state),
      paginatorSetup,
      t,
      userInfoStore,
      createProjectFormRef,
      editProjectFormRef,
      projectFormRules,
      projectTableRef,

      // icons
      Search,
      EditPen,
      Delete,
      Calendar,
      Document,
      ZoomIn,

      // function
      getFilteredProjects,
      updateDisplayList,

      onCreateProjectClicked,
      onSubmitNewProjectClicked,
      onSearchProjectClicked,
      onSaveModifyResultClicked,
      onEditProjectClicked,
      onDeleteProjectClicked,
      onClearSearchCriteriaClicked,
      onChangeSelect,
      onPagesizeChanged,
      onCurrentPageChanged,
      onSortChanged,
      onSiteListShown,
      onKaizenItemLinkClicked,
      onInputFieldEnterKeyUp,
      onColDisplayCtrlPanelHidded,
      onViewProjectClicked,

      // date
      dateFormat,

      // cell-style
      cellStyle,
      numeral,
      moment,
    };
  },
});
